/**
 * Admin Product Field Definitions API client — talks to
 * /api/admin-field-defs.php. All requests are credentialed (session cookie).
 */

import { CustomFieldType } from '../types';

const API_BASE = '/api';

export type FieldDefScope = 'trusscore' | 'retailer';
export type FieldOutputMode = 'replace' | 'alongside' | 'standalone';

export interface AdminFieldDef {
  id: number;
  fieldKey: string;
  label: string;
  dataType: CustomFieldType;
  scope: FieldDefScope;
  allRetailers: boolean;
  retailerIds: number[];
  urlTemplate: string | null;
  showInBom: boolean;
  showInPlacementGuide: boolean;
  columnHeading: string | null;
  outputMode: FieldOutputMode;
  columnOrder: number;
  productNumberHeading: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface FieldDefRetailerSummary {
  id: number;
  name: string;
  isPartner: boolean;
}

export interface FieldDefsListResponse {
  fieldDefs: AdminFieldDef[];
  retailers: FieldDefRetailerSummary[];
}

/** Writable shape; all optional for PATCH, key/label/scope required on create. */
export interface FieldDefPayload {
  fieldKey?: string;
  label?: string;
  dataType?: CustomFieldType;
  scope?: FieldDefScope;
  /** Convenience: 'all' sets allRetailers=1; 'select' uses retailerIds. */
  applyMode?: 'all' | 'select';
  allRetailers?: boolean;
  retailerIds?: number[];
  urlTemplate?: string | null;
  showInBom?: boolean;
  showInPlacementGuide?: boolean;
  columnHeading?: string | null;
  outputMode?: FieldOutputMode;
  columnOrder?: number;
  productNumberHeading?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchFieldDefs(): Promise<FieldDefsListResponse> {
  const res = await fetch(`${API_BASE}/admin-field-defs.php`, { credentials: 'include' });
  return handle<FieldDefsListResponse>(res);
}

export async function fetchFieldDef(id: number): Promise<{ fieldDef: AdminFieldDef; valueCount: number }> {
  const res = await fetch(`${API_BASE}/admin-field-defs.php?id=${id}`, { credentials: 'include' });
  return handle(res);
}

export async function createFieldDef(fieldDef: FieldDefPayload): Promise<{ fieldDef: AdminFieldDef }> {
  const res = await fetch(`${API_BASE}/admin-field-defs.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fieldDef }),
  });
  return handle(res);
}

export async function patchFieldDef(id: number, fieldDef: FieldDefPayload): Promise<{ fieldDef: AdminFieldDef }> {
  const res = await fetch(`${API_BASE}/admin-field-defs.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fieldDef }),
  });
  return handle(res);
}

export async function deleteFieldDef(id: number, confirmKey: string): Promise<void> {
  const params = new URLSearchParams({ id: String(id), confirmKey });
  const res = await fetch(`${API_BASE}/admin-field-defs.php?${params}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}
